import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, CalendarDays, Phone, User } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface BottomTabBarProps {
  currentTab: string;
  onTabChange: (tab: string) => void;
}
/**
 * Global scrim behind the floating tab bar: scrollable content dissolves into
 * the screen background before it can be read behind the navigation.
 * Fully transparent at the top, fully opaque across the navigation itself.
 */
const SCRIM_HEIGHT = 128;
const SCRIM_MASK = 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 56%, rgba(0,0,0,0.72) 72%, rgba(0,0,0,0.34) 86%, rgba(0,0,0,0) 100%)';
const SCRIM_LIGHT = 'linear-gradient(to top, #F5F5F4 0%, #F5F5F4 56%, rgba(245,245,244,0.92) 72%, rgba(245,245,244,0.58) 86%, rgba(245,245,244,0) 100%)';
const SCRIM_DARK = 'linear-gradient(to top, #0A0A0A 0%, #0A0A0A 56%, rgba(10,10,10,0.92) 72%, rgba(10,10,10,0.58) 86%, rgba(10,10,10,0) 100%)';
export function BottomTabBar({
  currentTab,
  onTabChange
}: BottomTabBarProps) {
  const {
    isLight
  } = useTheme();
  const tabs = [{
    id: 'home',
    label: 'Home',
    icon: Home
  }, {
    id: 'calls',
    label: 'Calls',
    icon: Phone
  }, {
    id: 'calendar',
    label: 'Calendar',
    icon: CalendarDays
  }, {
    id: 'profile',
    label: 'Account',
    icon: User
  }];
  return <div className="absolute bottom-0 left-0 right-0 z-40 flex justify-center pb-5 px-4 pointer-events-none" data-tour="bottom-tabs" data-id="element-596" data-mp="13bxiskdsh15v">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0" style={{
      height: SCRIM_HEIGHT,
      background: isLight ? SCRIM_LIGHT : SCRIM_DARK,
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      maskImage: SCRIM_MASK,
      WebkitMaskImage: SCRIM_MASK
    }} data-id="element-597" data-mp="uc11x4xn56ld" />
      <motion.nav initial={{
      y: 16,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      type: 'spring',
      stiffness: 420,
      damping: 32,
      delay: 0.1
    }} className={`pointer-events-auto w-full max-w-[290px] rounded-full px-2 py-[3px] backdrop-blur-2xl border transition-colors duration-500
          ${isLight ? 'bg-white border-slate-200 shadow-[0_-2px_12px_rgba(0,0,0,0.08),0_4px_20px_rgba(0,0,0,0.06)]' : 'bg-[#111111] border-white/[0.08] shadow-[0_-2px_12px_rgba(0,0,0,0.3),0_4px_24px_rgba(0,0,0,0.4)]'}`} data-id="element-598" data-mp="pesf2r1jseds">
        <div className="flex items-center justify-around" data-id="element-599" data-mp="30z00kgpzkoi7">
          {tabs.map(tab => {
          const isActive = currentTab === tab.id;
          const Icon = tab.icon;
          const isCalls = tab.id === 'calls';
          return <button key={tab.id} onClick={() => onTabChange(tab.id)} className="relative flex flex-col items-center justify-center w-[50px] py-1 rounded-xl transition-colors focus:outline-none" data-id="element-600" data-mp="1km1etj3ge1gw">
                {/* Active background indicator */}
                {isActive && <motion.div layoutId="activeTabBg" className={`absolute inset-0 rounded-xl ${isLight ? 'bg-vexu/[0.07]' : 'bg-vexu/[0.08]'}`} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 35
            }} data-id="element-601" data-mp="1kf966f4rnkc5" />}

                {/* Icon container */}
                <div className="relative z-10 flex items-center justify-center" data-id="element-602" data-mp="237jxrctyzvuy">
                  {isCalls && !isActive ? <div className={`w-7 h-7 rounded-full border flex items-center justify-center ${isLight ? 'bg-vexu/[0.04] border-vexu/[0.08]' : 'bg-vexu/[0.06] border-vexu/[0.1]'}`} data-id="element-603" data-mp="8sr3pi8qjd54">
                      <Icon className={`w-[15px] h-[15px] ${isLight ? 'text-vexu' : 'text-vexu/60'}`} strokeWidth={1.8} data-id="element-604" data-mp="247wgaokmy18b" />
                    </div> : isCalls && isActive ? <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isLight ? 'bg-vexu text-white shadow-sm shadow-vexu/15' : 'bg-vexu/15 text-vexu'}`} data-id="element-605" data-mp="28ajb8ycls29q">
                      <motion.div initial={{
                  scale: 0.85
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 25
                }} data-id="element-606" data-mp="1gjwp5njew8t7">
                        <Icon className="w-[15px] h-[15px]" strokeWidth={2.2} data-id="element-607" data-mp="wv4637a8q43j" />
                      </motion.div>
                    </div> : <motion.div animate={{
                y: isActive ? -0.5 : 0
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25
              }} data-id="element-608" data-mp="3d9prncoad9iy">
                      <Icon className={`w-[17px] h-[17px] transition-colors duration-200 ${isActive ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} strokeWidth={isActive ? 2.2 : 1.6} data-id="element-609" data-mp="3s7i5m0e6ppv1" />
                    </motion.div>}
                </div>

                {/* Label */}
                <motion.span animate={{
              opacity: isActive ? 1 : 0.45
            }} transition={{
              duration: 0.2
            }} className={`relative z-10 text-[9px] font-medium mt-0.5 tracking-wide transition-colors duration-200 ${isActive ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-610" data-mp="26vgmm4hwy0wb">
                  {tab.label}
                </motion.span>
              </button>;
        })}
        </div>
      </motion.nav>
    </div>;
}